import { useState, useMemo } from 'react';
import api from '../services/api';
import useGoldRateGate from './useGoldRateGate';

/**
 * useEstimateFlow
 * Owns all estimate-dialog UI state and handlers for SessionDetail.
 *
 * linkedEstimateDetails stays in SessionDetail (populated by fetchAll) and is
 * passed in as a param for handleConvertToOrder.
 */
export function useEstimateFlow({ session, sessionId, fetchAll, navigate, toast, linkedEstimateDetails, leadRepairEstimates }) {
  // ── Link-estimate dialog ──────────────────────────────────────────────────
  const [linkEstimateOpen, setLinkEstimateOpen] = useState(false);
  const [linkLeadTarget, setLinkLeadTarget] = useState(null);
  const [customerEstimates, setCustomerEstimates] = useState([]);

  // ── Create / duplicate estimate ───────────────────────────────────────────
  const [createEstimateOpen, setCreateEstimateOpen] = useState(false);
  const [createEstimateLead, setCreateEstimateLead] = useState(null);
  const [duplicateEstimateData, setDuplicateEstimateData] = useState(null);

  // ── View-estimate detail popup ────────────────────────────────────────────
  const [viewEstimateOpen, setViewEstimateOpen] = useState(false);
  const [viewEstimateData, setViewEstimateData] = useState(null);
  const [viewEstimateLeadContext, setViewEstimateLeadContext] = useState(null);

  // ── Link-to-lead prompt (customer changed on duplicate) ───────────────────
  const [showLinkPrompt, setShowLinkPrompt] = useState(false);
  const [pendingLinkEstimate, setPendingLinkEstimate] = useState(null);

  // ── Print prompt (shown after saving a new estimate) ─────────────────────
  const [showPrintPrompt, setShowPrintPrompt] = useState(false);
  const [printPromptEstimate, setPrintPromptEstimate] = useState(null);

  // ── Repair-estimate dialog (ledger) — for repair leads (main_group=="Repair") ──
  const [repairEstOpen, setRepairEstOpen] = useState(false);
  const [repairEstLead, setRepairEstLead] = useState(null);

  // ── Prefill for estimate form ─────────────────────────────────────────────
  const estimatePrefill = useMemo(() => ({
    customerPhone: session?.client_phone || '',
    customerName: session?.client_name || '',
    product: createEstimateLead?.product_name || '',
    mainGroup: createEstimateLead?.main_group || '',
  }), [session?.client_phone, session?.client_name, createEstimateLead?.product_name, createEstimateLead?.main_group]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const openLinkEstimate = async (lead) => {
    setLinkLeadTarget(lead);
    if (session?.client_phone) {
      try {
        const res = await api.get(`/estimates?phone=${session.client_phone}`);
        setCustomerEstimates(res.data || []);
      } catch (e) { setCustomerEstimates([]); }
    }
    setLinkEstimateOpen(true);
  };

  const handleLinkEstimate = async (estimateId) => {
    try {
      await api.patch(`/crm/leads/${linkLeadTarget._id}/link-estimate`, { estimate_id: estimateId });
      toast({ title: 'Estimate linked to lead' });
      setLinkEstimateOpen(false);
      fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  // ── Today-gold-rate gate (blocks estimate creation if today's rate missing) ──
  const goldGate = useGoldRateGate();

  const handleCreateEstimateForLead = async (lead) => {
    const target = lead || linkLeadTarget;
    // Repair leads use the two-sided charge/credit ledger, not the sales estimate form.
    if ((target?.main_group || '') === 'Repair') {
      setRepairEstLead(target);
      setRepairEstOpen(true);
      setLinkEstimateOpen(false);
      return;
    }
    const ok = await goldGate.check();
    if (!ok) return; // popup opens + cashier ping fires
    setCreateEstimateLead(target);
    setDuplicateEstimateData(null);
    setCreateEstimateOpen(true);
    setLinkEstimateOpen(false);
  };

  // Confirm a lead-stage repair estimate (RE-xxxx) → status "active".
  const handleConfirmRepairEstimate = async (est, e) => {
    if (e) e.stopPropagation();
    try {
      await api.post(`/repair-estimates/${est.id}/confirm`);
      toast({ title: `Repair estimate ${est.estimate_number} confirmed` });
      fetchAll();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const handleEstimateSaved = async (newEstimate) => {
    const leadId = createEstimateLead?._id;
    if (leadId && newEstimate?.id) {
      if (duplicateEstimateData) {
        const normalize = (s) => (s || '').trim().toLowerCase();
        const phoneChanged = normalize(newEstimate.customer_phone) !== normalize(duplicateEstimateData.customer_phone);
        const nameChanged = normalize(newEstimate.customer_name) !== normalize(duplicateEstimateData.customer_name);
        if (phoneChanged || nameChanged) {
          setPendingLinkEstimate({ estimateId: newEstimate.id, leadId });
          setShowLinkPrompt(true);
          setCreateEstimateLead(null);
          setDuplicateEstimateData(null);
          fetchAll();
          return;
        }
      }
      try {
        await api.patch(`/crm/leads/${leadId}/link-estimate`, { estimate_id: newEstimate.id });
      } catch (e) { console.error('Auto-link failed:', e); }
    }
    setCreateEstimateLead(null);
    setDuplicateEstimateData(null);
    fetchAll();
    setPrintPromptEstimate(newEstimate);
    setShowPrintPrompt(true);
  };

  const handleDuplicateEstimate = async (est) => {
    const ok = await goldGate.check();
    if (!ok) return;
    setDuplicateEstimateData(est);
    setCreateEstimateLead(viewEstimateLeadContext);
    setCreateEstimateOpen(true);
    setViewEstimateOpen(false);
  };

  const handleEstimateDeleted = () => fetchAll();

  const handleConfirmEstimate = async (est, e) => {
    e.stopPropagation();
    try {
      await api.patch(`/estimates/${est.estimate_number}/confirm`);
      toast({ title: `Estimate ${est.estimate_number} confirmed` });
      fetchAll();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const handleEstimateConfirmed = () => fetchAll();

  const openEstimateDetail = (est, lead = null) => {
    setViewEstimateData(est);
    setViewEstimateLeadContext(lead);
    setViewEstimateOpen(true);
  };

  const handleConvertToOrder = (lead) => {
    // ── Repair leads → Customer Repair order from a confirmed repair-estimate ledger ──
    if ((lead?.main_group || '') === 'Repair') {
      const repEsts = (leadRepairEstimates || {})[lead._id] || [];
      const rep = repEsts.find(e => e.status === 'active');
      if (!rep) {
        toast({ title: 'Confirm a repair estimate first', variant: 'destructive' });
        return;
      }
      // Derive which departments the repair needs, from the ledger's CHARGE lines.
      const materialsInvolved = { gold: false, diamond: false, stone: false };
      (rep.charges || []).forEach(c => {
        const t = (c.type || '').toLowerCase();
        if (t === 'new_gold') materialsInvolved.gold = true;
        else if (t === 'new_stone') { materialsInvolved.diamond = true; materialsInvolved.stone = true; }
      });
      navigate('/messenger', {
        state: {
          fromRepairEstimate: true,
          repairData: {
            customerPhone: rep.customer_phone || session?.client_phone || '',
            customerName: rep.customer_name || session?.client_name || '',
            mainGroup: 'Repair',
            title: lead.product_name || 'Repair',
            productWeight: lead.repair_weight != null ? lead.repair_weight.toString() : '',
            specialInstructions: lead.description || '',
            priceAmount: (rep.net_amount != null ? rep.net_amount : 0).toString(),
            materialsInvolved,
            repairEstimateId: rep.id,
            repairEstimateNumber: rep.estimate_number,
          },
          fromLead: true,
          leadId: lead._id,
          sessionId,
        },
      });
      return;
    }

    const estimates = (linkedEstimateDetails || {})[lead._id] || [];
    const est = estimates.find(e => e.status === 'confirmed');
    if (!est) {
      toast({ title: 'No confirmed estimate found', variant: 'destructive' });
      return;
    }
    // Inventory-only stone summary (NO pricing) — this text is visible to the
    // production team, so it must never carry rates/amounts.
    const stoneDetailsText = est.stones?.length > 0
      ? est.stones.map(s => {
          const parts = [s.name || 'Stone'];
          if (s.weight) parts.push(`${s.weight}ct`);
          if (s.count) parts.push(`×${s.count}`);
          return parts.join(' ');
        }).join(', ')
      : '';
    // Auto-derive which departments this order needs, from the estimate's stones + gold.
    const materialsInvolved = { gold: false, diamond: false, stone: false };
    (est.stones || []).forEach(s => {
      const kind = (s.kind || '').toString().toLowerCase();
      if (kind === 'diamond') materialsInvolved.diamond = true;
      else if (kind === 'colour_stone' || kind === 'color_stone' || kind === 'colour stone') materialsInvolved.stone = true;
      else if (kind) materialsInvolved.stone = true; // unknown stone kind → treat as colour stone
    });
    if ((est.gross_weight || 0) > 0 || est.gold_purity || est.gold_rate) materialsInvolved.gold = true;
    navigate('/messenger', {
      state: {
        fromEstimate: true,
        estimateData: {
          customerPhone: est.customer_phone,
          customerName: est.customer_name,
          mainGroup: est.main_group || '',
          title: est.product || '',
          productWeight: est.gross_weight?.toString() || '',
          karatPurity: est.gold_purity || '',
          goldRatePerGram: est.gold_rate?.toString() || '',
          goldRateType: 'fixed',
          stoneDetails: stoneDetailsText,
          priceAmount: est.total_amount?.toString() || '',
          materialsInvolved,
          linkedEstimateId: est.id,
          estimateNumber: est.estimate_number,
        },
        fromLead: true,
        leadId: lead._id,
        sessionId,
      },
    });
  };

  return {
    // States (for JSX bindings)
    linkEstimateOpen, setLinkEstimateOpen,
    linkLeadTarget, setLinkLeadTarget,
    customerEstimates,
    createEstimateOpen, setCreateEstimateOpen,
    createEstimateLead,
    duplicateEstimateData,
    viewEstimateOpen, setViewEstimateOpen,
    viewEstimateData, setViewEstimateData,
    viewEstimateLeadContext,
    showLinkPrompt, setShowLinkPrompt,
    pendingLinkEstimate, setPendingLinkEstimate,
    showPrintPrompt, setShowPrintPrompt,
    printPromptEstimate,
    repairEstOpen, setRepairEstOpen,
    repairEstLead,
    estimatePrefill,
    // Handlers
    openLinkEstimate,
    handleLinkEstimate,
    handleCreateEstimateForLead,
    handleConfirmRepairEstimate,
    handleEstimateSaved,
    handleDuplicateEstimate,
    handleEstimateDeleted,
    handleConfirmEstimate,
    handleEstimateConfirmed,
    openEstimateDetail,
    handleConvertToOrder,
    // Gold-rate gate (render <GoldRateGateDialog {...goldGateDialogProps} /> once)
    goldGateDialogProps: goldGate.dialogProps,
  };
}
